import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import { useIsDesktopBp } from 'hooks/breakpoints';
import { useGeolocation } from 'state/geolocation';
import { STORE_LOCATOR_SEARCH_INPUT } from 'utils/test-ids';

import {
  focusStoreLocatorFirstRestaurantResult,
  requestStoreLocatorFirstRestaurantFocus,
} from '../store-locator-focus-handoff';

import { ISearchBarProps } from './types';

export const SearchBar = ({
  defaultValue,
  addressHeadingNativeId,
  label,
  autoFocus,
  onBlur,
}: ISearchBarProps) => {
  const { formatMessage } = useIntl();
  const isDesktop = useIsDesktopBp();
  const { setManualAddress, updateManualCoordinates, manualAddress, isPermissionGranted } =
    useGeolocation();

  const [inputValue, setInputValue] = useState<string | undefined>(
    defaultValue || manualAddress || ''
  );
  const selectedAddressForFocusRef = useRef<string | null>(null);

  const handleInputValueChange = useCallback((address: string | undefined) => {
    if (selectedAddressForFocusRef.current && address !== selectedAddressForFocusRef.current) {
      selectedAddressForFocusRef.current = null;
    }

    setInputValue(address);
  }, []);

  const onInputValueChange = useCallback(
    (address: string | undefined | null) => {
      setManualAddress(address || '');
    },
    [setManualAddress]
  );

  const headingLinked = Boolean(addressHeadingNativeId);
  const fieldLabel = label ?? formatMessage({ id: 'enterYourDeliveryAddress' });

  return (
    <AutocompleteAddressInput
      autoFocus={autoFocus ?? (!manualAddress && isPermissionGranted && isDesktop)}
      required
      value={inputValue}
      onInputValueChange={handleInputValueChange}
      onAddressSelected={(address, placeId) => {
        if (address) {
          selectedAddressForFocusRef.current = address;
          requestStoreLocatorFirstRestaurantFocus();
        } else {
          selectedAddressForFocusRef.current = null;
        }
        setInputValue(address);
        updateManualCoordinates(address, placeId);
      }}
      onWebInputTab={event => {
        if (!selectedAddressForFocusRef.current) {
          return;
        }

        if (focusStoreLocatorFirstRestaurantResult()) {
          event.preventDefault();
          selectedAddressForFocusRef.current = null;
        }
      }}
      onBlur={() => {
        onInputValueChange(inputValue);
        onBlur?.(inputValue ?? '');
      }}
      label={fieldLabel}
      fieldLabelledBy={headingLinked ? addressHeadingNativeId : undefined}
      accessibilityLabelWhenLabelledByNative={headingLinked ? fieldLabel : undefined}
      testID={STORE_LOCATOR_SEARCH_INPUT}
    />
  );
};

export default SearchBar;
